import { z } from 'zod';
import { paginationQuerySchema } from '../pagination.js';
import { hasAtLeastRole } from '../roles.js';
/**
 * Types de lots d'import.
 *
 * Seule la fiche article (~450 lignes) peut encore etre deposee seule et traitee
 * de maniere synchrone. Les quatre sources de mouvements **et l'inventaire
 * d'ancrage** (~9 900 lignes, dans le meme classeur de 87 Mo) passent par le
 * worker_thread (voir « Architecture d'import »).
 */
export const IMPORT_TYPES = [
    'FICHE_ARTICLE',
    'STOCK_INITIAL',
    'COPACKING',
    'NOMENCLATURE',
    'GERECD',
    'GESUPD',
    'JNL_NON_SYSTEME',
    'GEJNM',
    'LIBELLES_MOUVEMENTS',
];
export const importTypeSchema = z.enum(IMPORT_TYPES);
export const IMPORT_TYPE_LABELS = {
    FICHE_ARTICLE: 'Fiche article',
    STOCK_INITIAL: 'Inventaire d’ancrage',
    COPACKING: 'Copacking',
    NOMENCLATURE: 'Nomenclature',
    GERECD: 'Réceptions (GERECD)',
    GESUPD: 'Livraisons (GESUPD)',
    JNL_NON_SYSTEME: 'Journal des mouvements non-système',
    GEJNM: 'Comptabilité matières (GEJNM)',
    LIBELLES_MOUVEMENTS: 'Libellés des mouvements',
};
export const IMPORT_STATUSES = [
    'QUEUED',
    'RUNNING',
    'DONE',
    'DONE_WITH_REJECTS',
    'DONE_WITH_ANOMALY',
    'AWAITING_CONFIRMATION',
    'FAILED',
];
export const IMPORT_STATUS_LABELS = {
    QUEUED: 'En attente',
    RUNNING: 'En cours',
    DONE: 'Terminé',
    DONE_WITH_REJECTS: 'Terminé avec rejets',
    DONE_WITH_ANOMALY: 'Terminé avec anomalie',
    AWAITING_CONFIRMATION: 'En attente de confirmation',
    FAILED: 'Échec',
};
/**
 * Taille maximale acceptee pour un referentiel (traitement synchrone assume).
 */
export const MAX_SYNC_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
/**
 * Taille maximale acceptee pour une source de mouvements. Le spike ExcelJS
 * (docs/spike-exceljs.md) mesure un RSS d'environ 3,2 x la taille du fichier :
 * 200 Mo correspond a ~640 Mo de memoire, ce qui reste dans l'enveloppe.
 */
export const MAX_IMPORT_FILE_BYTES = 200 * 1024 * 1024;
/** Types traites de maniere synchrone dans la requete HTTP. */
export const SYNCHRONOUS_IMPORT_TYPES = ['FICHE_ARTICLE'];
/** Types traites en arriere-plan dans un worker_thread. */
export const BACKGROUND_IMPORT_TYPES = [
    'STOCK_INITIAL',
    'COPACKING',
    'NOMENCLATURE',
    'GERECD',
    'GESUPD',
    'JNL_NON_SYSTEME',
    'GEJNM',
    'LIBELLES_MOUVEMENTS',
];
export function isBackgroundImport(type) {
    return BACKGROUND_IMPORT_TYPES.includes(type);
}
/**
 * Types d'import que seul un ADMIN depose (decision de Sam du 28/10/2026).
 *
 * Les libelles de KN font foi et ecrasent un libelle saisi a l'ecran : c'est
 * du parametrage, pas l'import quotidien. Liste unique, lue par l'API (garde
 * de la route de depot) comme par l'ecran (types proposes).
 */
export const IMPORT_TYPES_RESERVES_ADMIN = ['LIBELLES_MOUVEMENTS'];
/** Ce role peut-il deposer ce type ? Le depot demande toujours OPERATEUR+. */
export function peutDeposerImport(role, type) {
    if (!hasAtLeastRole(role, 'OPERATEUR'))
        return false;
    return role === 'ADMIN' || !IMPORT_TYPES_RESERVES_ADMIN.includes(type);
}
/** Nombre maximal de rejets detailles conserves dans un rapport. */
export const MAX_REPORTED_REJECTS = 500;
/**
 * Options passees a l'upload. Le stock initial demande une date d'inventaire
 * (defaut 01/01/2026) et un mapping de colonnes optionnel.
 */
export const importOptionsSchema = z.object({
    dateInventaire: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .default('2026-01-01'),
    /** Mapping « champ cible -> en-tete de colonne dans le fichier ». */
    colonnes: z.record(z.string().trim().min(1)).optional(),
    /**
     * Feuille a traiter. Les exports Generix sont livres dans un **classeur
     * multi-feuilles** : sans precision, l'import retient la premiere feuille
     * dont le nom correspond aux alias du type de lot.
     */
    feuille: z.string().trim().max(120).optional(),
    /**
     * Bornes de journees a integrer. Sert aux **imports de rattrapage** depuis une
     * extraction plus ancienne : sans borne, le mecanisme de remplacement laisse
     * l'ancienne extraction ecraser des journees plus recentes.
     * Hors rattrapage, laisser vide : la fenetre glissante quotidienne s'appuie
     * sur l'idempotence par journee.
     */
    journeeMin: z
        .string()
        .regex(/^d{4}-d{2}-d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    /** Confirmation OPERATEUR d un rattrapage (regle 2). */
    confirmerRattrapage: z.coerce.boolean().optional(),
    journeeMax: z
        .string()
        .regex(/^d{4}-d{2}-d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
});
export const listImportsQuerySchema = paginationQuerySchema.extend({
    type: importTypeSchema.optional(),
});
//# sourceMappingURL=import.js.map